import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { MANAGEMENT_TABS_SX } from "components/management/managementTheme";
import {
  IntegrationDetail,
  IntegrationStepItem,
  ReadinessIssue,
  useGetIntegrationReadinessQuery,
} from "features/integrationsBeta/integrationsBeta";
import {
  issuesBySection,
  worstSeverity,
  type SettingsSection,
} from "features/integrationsBeta/readinessUi";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { useGetWebhookTriggersQuery } from "features/integrationsBeta/webhookTriggers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useState } from "react";
import ConnectionMasterDetail, {
  type ConnectionGroup,
} from "./ConnectionMasterDetail";
import DangerZoneSection from "./DangerZoneSection";
import ExecutionTargetSection from "./ExecutionTargetSection";
import IntegrationConfigForm from "./IntegrationConfigForm";
import {
  PendingChangesProvider,
  usePendingChanges,
} from "./pending/PendingChangesContext";
import PendingReviewDialog from "./pending/PendingReviewDialog";
import PendingSaveBar from "./pending/PendingSaveBar";
import ReadinessBanner from "./ReadinessBanner";
import SchedulesSection from "./SchedulesSection";
import SectionCard from "./SectionCard";
import StepsSection from "./StepsSection";
import WebhookTriggerPanel from "./WebhookTriggerPanel";

interface Props {
  integration: IntegrationDetail;
  /** Lifted dirty state so the page can guard tab switches / Back. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Fired after the integration was deleted from the Danger Zone tab. */
  onDeleted?: () => void;
}

function uniqueConnectionIds(
  steps: IntegrationStepItem[],
  stage: string,
): number[] {
  const ids = steps
    .filter((s) => s.stage === stage && s.connection != null)
    .map((s) => s.connection as number);
  return Array.from(new Set(ids));
}

export default function SettingsPanel({
  integration,
  onDirtyChange,
  onDeleted,
}: Props) {
  return (
    <PendingChangesProvider onDirtyChange={onDirtyChange}>
      <SettingsPanelBody integration={integration} onDeleted={onDeleted} />
    </PendingChangesProvider>
  );
}

function SettingsPanelBody({
  integration,
  onDeleted,
}: {
  integration: IntegrationDetail;
  onDeleted?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { isSuperuser } = usePlatformRole();
  const { changes, stage, getPending, removeKeys } = usePendingChanges();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [section, setSection] = useState<SettingsSection>("status");
  // Connection the Connections tab should select (banner / step-chip jumps);
  // the nonce re-fires the selection when the same id is requested again.
  const [connectionFocus, setConnectionFocus] = useState<{
    id: number;
    nonce: number;
  } | null>(null);

  const { data: readiness } = useGetIntegrationReadinessQuery(integration.id);
  const { data: triggers = [], isLoading: triggersLoading } =
    useGetWebhookTriggersQuery(integration.id);

  // Trigger mode is derived on the backend from the pipeline's steps. Only
  // the relevant trigger section is shown; the "wrong" side stays visible
  // in delete-only mode while orphaned rows still exist (hiding them would
  // make cleanup impossible).
  const triggerMode = integration.trigger_mode;
  const modeKnown = triggerMode !== undefined;
  const isWebhookMode = triggerMode === "WEBHOOK";
  const orphanSchedules =
    modeKnown && isWebhookMode && integration.schedules.length > 0;
  const orphanTriggers = modeKnown && !isWebhookMode && triggers.length > 0;
  const showWebhooksTab = !modeKnown || isWebhookMode || orphanTriggers;
  const showSchedulesTab = !modeKnown || !isWebhookMode || orphanSchedules;

  useEffect(() => {
    if (
      (section === "webhooks" && !showWebhooksTab) ||
      (section === "schedules" && !showSchedulesTab) ||
      (section === "danger" && !isSuperuser)
    ) {
      setSection("status");
    }
  }, [section, showWebhooksTab, showSchedulesTab, isSuperuser]);

  // Reconciliation: entities deleted (here or externally) take their staged
  // records with them, so the review dialog never references ghosts.
  useEffect(() => {
    if (triggersLoading) return;
    const stepIds = new Set(integration.steps.map((s) => s.id));
    const scheduleIds = new Set(integration.schedules.map((s) => s.id));
    const triggerIds = new Set(triggers.map((t) => t.id));
    const orphans = changes.filter(
      (c) =>
        (c.entityType === "step" && !stepIds.has(c.entityId)) ||
        (c.entityType === "schedule" && !scheduleIds.has(c.entityId)) ||
        (c.entityType === "webhookTrigger" && !triggerIds.has(c.entityId)),
    );
    if (orphans.length) {
      removeKeys(orphans.map((o) => o.key));
      dispatch(
        pushNotification({
          severity: "info",
          message: "Pending changes for deleted items were discarded.",
        }),
      );
    }
  }, [
    integration.steps,
    integration.schedules,
    triggers,
    triggersLoading,
    changes,
    removeKeys,
    dispatch,
  ]);

  const connectionGroups = useMemo<ConnectionGroup[]>(() => {
    const seen = new Set<number>();
    const dedupe = (ids: number[]) =>
      ids.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    return [
      {
        label: "Sources",
        ids: dedupe(uniqueConnectionIds(integration.steps, "SOURCE")),
      },
      {
        label: "Transforms",
        ids: dedupe(uniqueConnectionIds(integration.steps, "TRANSFORM")),
      },
      {
        label: "Destinations",
        ids: dedupe(uniqueConnectionIds(integration.steps, "DESTINATION")),
      },
      {
        label: "Webhook",
        ids: dedupe(Array.from(new Set(triggers.map((t) => t.connection)))),
      },
    ].filter((group) => group.ids.length > 0);
  }, [integration.steps, triggers]);

  const grouped = useMemo(
    () => issuesBySection(readiness?.issues),
    [readiness],
  );
  const issuesByConnection = useMemo(() => {
    const map: Record<number, ReadinessIssue[]> = {};
    (readiness?.issues ?? []).forEach((issue) => {
      if (issue.connection_id != null) {
        (map[issue.connection_id] ??= []).push(issue);
      }
    });
    return map;
  }, [readiness]);

  const pendingSections = useMemo(
    () => new Set(changes.map((c) => c.section)),
    [changes],
  );

  const focusConnection = (id: number | null | undefined) => {
    if (id != null) setConnectionFocus({ id, nonce: Date.now() });
  };

  const openSection = (target: SettingsSection, issue?: ReadinessIssue) => {
    setSection(target);
    if (target === "connections") focusConnection(issue?.connection_id);
  };

  // Inline counter bubble (not an absolutely-positioned MUI Badge, which
  // gets clipped by the tab's bounds) + a warning dot for staged changes.
  const tabLabel = (label: string, value: SettingsSection) => {
    const issues = grouped[value];
    const hasPending = pendingSections.has(value);
    if (!issues?.length && !hasPending) return label;
    const severity = issues?.length ? worstSeverity(issues) : null;
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
      >
        {label}
        {issues?.length ? (
          <Box
            component="span"
            sx={{
              bgcolor: severity === "error" ? "error.main" : "warning.main",
              color: "common.white",
              borderRadius: "9px",
              minWidth: 18,
              height: 18,
              px: 0.5,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {issues.length}
          </Box>
        ) : null}
        {hasPending && (
          <Box
            component="span"
            aria-label="Has unsaved changes"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "warning.main",
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    );
  };

  const pendingActive = getPending("integration", integration.id, "is_active");
  const effectiveActive =
    (pendingActive?.newValue as boolean | undefined) ?? integration.is_active;

  return (
    <Stack spacing={2} sx={{ minHeight: "100%" }}>
      {!isSuperuser && (
        <Typography variant="body2" color="text.secondary">
          You can view settings. Only a superuser can make changes.
        </Typography>
      )}

      <ReadinessBanner
        readiness={readiness}
        onNavigate={(target, issue) => openSection(target, issue)}
      />

      <Box
        sx={{
          // Pin the sub-tab bar to the top of the Settings scroll container
          // so it stays put while section content scrolls beneath it.
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={section}
          onChange={(_, v) => setSection(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={MANAGEMENT_TABS_SX}
        >
          <Tab label={tabLabel("Status", "status")} value="status" />
          <Tab
            label={tabLabel("Connections", "connections")}
            value="connections"
          />
          <Tab label={tabLabel("Pipeline Steps", "steps")} value="steps" />
          <Tab
            label={tabLabel("Integration Config", "integration_config")}
            value="integration_config"
          />
          <Tab label={tabLabel("Execution", "execution")} value="execution" />
          {showWebhooksTab && (
            <Tab label={tabLabel("Webhooks", "webhooks")} value="webhooks" />
          )}
          {showSchedulesTab && (
            <Tab
              label={tabLabel("Schedulers", "schedules")}
              value="schedules"
            />
          )}
          {/* Hidden rather than disabled for non-superusers: the page is
              reachable by calibrate managers in view-only mode, and a dead red
              tab reads worse than no tab. */}
          {isSuperuser && (
            <Tab
              label="Danger Zone"
              value="danger"
              // Doubled selectors on purpose: MANAGEMENT_TABS_SX styles this
              // tab from the parent ("& .MuiTab-root.Mui-selected"), which
              // outranks a plain child rule, so the accent orange would win.
              sx={{
                "&&": { color: "error.main" },
                "&&.Mui-selected": { color: "error.main" },
              }}
            />
          )}
        </Tabs>
      </Box>

      {section === "status" && (
        <SectionCard title="Status">
          <FormControlLabel
            control={
              <Switch
                checked={effectiveActive}
                disabled={!isSuperuser}
                onChange={(_, checked) =>
                  stage({
                    entityType: "integration",
                    entityId: integration.id,
                    section: "status",
                    entityLabel: "Integration",
                    field: "is_active",
                    fieldLabel: "Status",
                    valueKind: "enabled",
                    oldValue: integration.is_active,
                    newValue: checked,
                  })
                }
              />
            }
            label={effectiveActive ? "Enabled" : "Disabled"}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Disabling an integration also disables all of its schedules.
          </Typography>
        </SectionCard>
      )}

      {section === "connections" && (
        <SectionCard title="Connections">
          <ConnectionMasterDetail
            groups={connectionGroups}
            focus={connectionFocus}
            issuesByConnection={issuesByConnection}
            clientId={integration.client}
          />
        </SectionCard>
      )}

      {section === "steps" && (
        <StepsSection
          integration={integration}
          issues={grouped.steps ?? []}
          onNavigateToConnection={(id) => {
            setSection("connections");
            focusConnection(id);
          }}
        />
      )}
      {section === "integration_config" && (
        <IntegrationConfigForm integration={integration} />
      )}
      {section === "execution" && (
        <ExecutionTargetSection integration={integration} />
      )}
      {section === "webhooks" && (
        <WebhookTriggerPanel
          integrationId={integration.id}
          orphanMode={orphanTriggers}
        />
      )}

      {section === "schedules" && (
        <SchedulesSection
          integrationId={integration.id}
          timezone={integration.timezone}
          orphanMode={orphanSchedules}
        />
      )}

      {section === "danger" && isSuperuser && (
        <DangerZoneSection integration={integration} onDeleted={onDeleted} />
      )}

      <PendingSaveBar onReview={() => setReviewOpen(true)} />
      {reviewOpen && (
        <PendingReviewDialog
          open
          onClose={() => setReviewOpen(false)}
          integrationId={integration.id}
        />
      )}
    </Stack>
  );
}
